/**
 * Grand Quack Auto — synthesized audio effects.
 *
 * All sounds are generated via Web Audio API — no files, zero bundle cost.
 * Browser autoplay policy requires user interaction before AudioContext works.
 */

import { getCtx, getDestination } from './audioMixer';
import { stopBackgroundMusic } from './musicSynth';

export { getCtx };

let audioActive = true;

/** Returns false after stopAllAudio — used by other modules to skip new sounds. */
export function isAudioActive(): boolean { return audioActive; }

/** Short crash/impact sound: noise burst + low thump. */
export function playCrashSound(): void {
  const c = getCtx();
  const now = c.currentTime;

  // Noise burst.
  const bufSize = c.sampleRate * 0.1;
  const buf = c.createBuffer(1, bufSize, c.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < bufSize; i++) data[i] = (Math.random() * 2 - 1) * 0.3;
  const noise = c.createBufferSource();
  noise.buffer = buf;

  const noiseGain = c.createGain();
  noiseGain.gain.setValueAtTime(0.3, now);
  noiseGain.gain.exponentialRampToValueAtTime(0.01, now + 0.15);
  noise.connect(noiseGain).connect(getDestination());
  noise.start(now);
  noise.stop(now + 0.15);

  // Low thump.
  const osc = c.createOscillator();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(80, now);
  osc.frequency.exponentialRampToValueAtTime(30, now + 0.2);
  const g = c.createGain();
  g.gain.setValueAtTime(0.4, now);
  g.gain.exponentialRampToValueAtTime(0.01, now + 0.25);
  osc.connect(g).connect(getDestination());
  osc.start(now);
  osc.stop(now + 0.3);
}

// ── Cop siren (looping oscillating tone) ──────────────────────────────────

let sirenOsc: OscillatorNode | null = null;
let sirenGain: GainNode | null = null;

export function startSiren(): void {
  if (sirenOsc) return;
  const c = getCtx();
  sirenOsc = c.createOscillator();
  sirenOsc.type = 'sine';
  // Oscillate between two frequencies for wail effect.
  sirenOsc.frequency.setValueAtTime(600, c.currentTime);
  const now = c.currentTime;
  for (let t = 0; t < 30; t++) {
    sirenOsc.frequency.linearRampToValueAtTime(800, now + t + 0.5);
    sirenOsc.frequency.linearRampToValueAtTime(600, now + t + 1);
  }
  sirenGain = c.createGain();
  sirenGain.gain.value = 0.08;
  sirenOsc.connect(sirenGain).connect(getDestination());
  sirenOsc.start();
}

export function stopSiren(): void {
  if (sirenOsc) {
    sirenOsc.stop();
    sirenOsc.disconnect();
    sirenOsc = null;
  }
  if (sirenGain) {
    sirenGain.disconnect();
    sirenGain = null;
  }
}

// ── Car engine sound (GTA-style rumble that revs with speed) ──────────────

let engineOsc: OscillatorNode | null = null;
let engineGain: GainNode | null = null;

export function startEngine(): void {
  if (engineOsc) return;
  const c = getCtx();
  if (c.state === 'suspended') c.resume();
  engineOsc = c.createOscillator();
  engineOsc.type = 'sawtooth';
  engineOsc.frequency.value = 60; // idle rumble

  const filter = c.createBiquadFilter();
  filter.type = 'lowpass';
  filter.frequency.value = 200;
  filter.Q.value = 2;

  engineGain = c.createGain();
  engineGain.gain.value = 0.06;

  engineOsc.connect(filter).connect(engineGain).connect(getDestination());
  engineOsc.start();
}

export function updateEngineSound(speed: number): void {
  if (!engineOsc || !engineGain) return;
  // Frequency rises with speed: idle 60Hz → full throttle 180Hz.
  const freq = 60 + Math.min(Math.abs(speed), 30) * 4;
  engineOsc.frequency.setTargetAtTime(freq, getCtx().currentTime, 0.1);
  // Volume rises slightly with speed.
  const vol = 0.04 + Math.min(Math.abs(speed), 30) * 0.003;
  engineGain.gain.setTargetAtTime(vol, getCtx().currentTime, 0.1);
}

export function stopEngine(): void {
  if (engineOsc) { engineOsc.stop(); engineOsc.disconnect(); engineOsc = null; }
  if (engineGain) { engineGain.disconnect(); engineGain = null; }
}

// ── Mother's Rage heartbeat (60 BPM lub-dub thump) ────────────────────────

let heartbeatInterval: ReturnType<typeof setInterval> | null = null;

/** Schedule a single lub-dub thump at AudioContext time `at`. */
function scheduleHeartbeatThump(at: number): void {
  const c = getCtx();
  for (const [tOff, freq] of [[0, 50], [0.18, 38]] as const) {
    const osc = c.createOscillator();
    osc.type = 'sine';
    osc.frequency.value = freq;
    const g = c.createGain();
    const start = at + tOff;
    g.gain.setValueAtTime(0, start);
    g.gain.linearRampToValueAtTime(0.45, start + 0.04);
    g.gain.exponentialRampToValueAtTime(0.001, start + 0.22);
    osc.connect(g).connect(getDestination());
    osc.start(start);
    osc.stop(start + 0.25);
  }
}

/** Start the heartbeat loop. Idempotent. Called by juiceSystem.enterRage(). */
export function startHeartbeat(): void {
  if (heartbeatInterval) return;
  const c = getCtx();
  if (c.state === 'suspended') c.resume();
  // Fire one immediately + then every second (60 BPM).
  scheduleHeartbeatThump(c.currentTime);
  heartbeatInterval = setInterval(() => {
    scheduleHeartbeatThump(getCtx().currentTime);
  }, 1000);
}

/** Stop the heartbeat loop. Called by juiceSystem.exitRage(). */
export function stopHeartbeat(): void {
  if (heartbeatInterval) {
    clearInterval(heartbeatInterval);
    heartbeatInterval = null;
  }
}

// ── Global audio shutdown (death/end screen) ─────────────────────────────

/** Kill all persistent audio. Called by deathScreen and statsScreen. */
export function stopAllAudio(): void {
  audioActive = false;
  stopSiren();
  stopEngine();
  stopHeartbeat();
  stopBackgroundMusic();
}


